<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    export let searchValue: string = '';
    export let searchPlaceholder: string = 'Search…';
    export let searchLabel: string = 'Search';
    export let searchMinChars: number = 3;
    export let resultsCount: number | null = null;
    export let resultsSingular: string = 'result';
    export let resultsPlural: string = 'results';

    const dispatch = createEventDispatcher();

    function onInput(e: Event) {
        const value = (e.target as HTMLInputElement).value;
        searchValue = value;
        dispatch('search', value);
    }

    function clear() {
        searchValue = '';
        dispatch('search', '');
    }
</script>

<div class="app-toolbar">
    <div class="search-wrap">
        <i class="search icon" aria-hidden="true"></i>
        <input
            type="search"
            class="search-input"
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
            autocomplete="off"
            spellcheck="false"
            value={searchValue}
            on:input={onInput}
        />
        {#if searchValue}
            <button type="button" class="clear-btn" on:click={clear} aria-label="Clear search">
                <i class="times icon" aria-hidden="true"></i>
            </button>
        {/if}
        {#if searchValue.length > 0 && searchValue.length < searchMinChars}
            <div class="search-hint" aria-live="polite">
                Type {searchMinChars - searchValue.length} more character{searchMinChars - searchValue.length === 1 ? '' : 's'}
            </div>
        {/if}
    </div>

    <div class="toolbar-right">
        <slot name="controls" />
        {#if resultsCount !== null}
            <span class="results-count">
                {resultsCount} {resultsCount === 1 ? resultsSingular : resultsPlural}
            </span>
        {/if}
    </div>
</div>

<style>
    .app-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        margin-bottom: var(--space-5);
        flex-wrap: wrap;
    }

    .search-wrap {
        position: relative;
        flex: 1;
        min-width: 280px;
        max-width: 500px;
    }

    .search-wrap :global(i.search.icon) {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--grey-secondary);
        pointer-events: none;
    }

    .search-input {
        width: 100%;
        padding: var(--space-3) var(--space-5) var(--space-3) var(--space-8);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        background: var(--surface);
        font: inherit;
        color: var(--grey);
    }

    .search-input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    .clear-btn {
        position: absolute;
        right: var(--space-2);
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        color: var(--grey-secondary);
        cursor: pointer;
        padding: var(--space-2);
        border-radius: var(--radius-input);
    }

    .clear-btn:hover { background: var(--surface-hover); color: var(--grey); }
    .clear-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .search-hint {
        position: absolute;
        bottom: -1.5rem;
        left: var(--space-8);
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        font-style: italic;
    }

    .toolbar-right {
        display: flex;
        align-items: center;
        gap: var(--space-4);
    }

    .results-count {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
        white-space: nowrap;
    }

    @media (max-width: 768px) {
        .app-toolbar { flex-direction: column; align-items: stretch; }
        .search-wrap { max-width: none; }
    }
</style>
